import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { ReactNode, useState } from 'react'

export interface SummaryProps {
  title: string
  children: ReactNode
  initiallyOpen?: boolean
  className?: string
  titleClassName?: string
  bordered?: boolean
  defaultHeight?: number
}

export function Summary({
  title,
  children,
  initiallyOpen = false,
  bordered = true,
  defaultHeight = 42,
  className,
  titleClassName,
}: SummaryProps) {
  const [hiddenContent, setHiddenContent] = useState<HTMLElement | null>(null)

  return (
    <Disclosure defaultOpen={initiallyOpen}>
      {({ open }) => (
        <div
          className={classNames(
            'relative w-full border bg-card rounded-[3px] border-standard-xlight h-fit space-y-4',
            'transition-[max-height] will-change-[max-height] duration-200 ease-in-out p-2 overflow-hidden',
            className,
            { 'border-1': bordered, 'border-0': !bordered }
          )}
          style={{
            maxHeight: open
              ? (hiddenContent?.offsetHeight ?? 0) + defaultHeight
              : defaultHeight,
          }}
        >
          <DisclosureButton className='flex items-center justify-between w-full text-md !bg-transparent !border-none text-basic !px-0'>
            <span className={classNames('font-bold', titleClassName)}>
              {title}
            </span>
            <ChevronRightIcon
              className={classNames(
                'w-4 h-4 transition-transform duration-200 ease-in-out stroke-current stroke-2',
                { 'rotate-0': !open, 'rotate-90': open }
              )}
            />
          </DisclosureButton>
          <DisclosurePanel static>{children}</DisclosurePanel>
          <span
            className='absolute opacity-0 pointer-events-none z-[-1] p-2'
            ref={setHiddenContent}
          >
            {children}
          </span>
        </div>
      )}
    </Disclosure>
  )
}
